import type { RawGraphNode2 } from '@ardoq/graph';
import { HierarchicalTreemapNode } from '../types';
import type { ArdoqId } from '@ardoq/api-types';
import type { RepresentationData } from '@ardoq/data-model';
import { getLuminance } from 'polished';
import { LUMINANCE_THRESHOLD } from 'tabview/consts';

type ReduceNodesState = { nodes: HierarchicalTreemapNode[] };

/**
 * What a raw graph item needs looking up to become a drawable node. Resolvers rather than
 * dictionaries because the two modes answer them differently: workspace mode has already
 * resolved every component in the collection, while viewpoint mode asks the scope-data
 * interfaces per item — and only a group knows how to label itself, so the label resolver
 * takes the item rather than an id.
 */
type NodeResolvers = {
  resolveLabel: (item: RawGraphNode2) => string | null;
  resolveRepresentationData: (modelId: ArdoqId) => RepresentationData | null;
  resolveColor: (modelId: ArdoqId) => string | null;
  /**
   * Whether a `modelId` names a component this scope data actually has.
   *
   * `RawGraphNode2.modelId` is documented as the component or reference id, but `buildGroups` puts
   * whatever the rule grouped *by* in it: a component type id for `GroupType.COMPONENT`, a
   * workspace id for `GroupType.WORKSPACE`, the ancestor's id for the parent and reference rules,
   * and nothing at all for field, tag and all. Only the middle group is a component.
   *
   * Asked rather than inferred from `type`, because the parent and reference groups are genuinely
   * components and should keep their icon and colour.
   */
  isComponent: (modelId: ArdoqId) => boolean;
};

/**
 * Joined the same way both `getGraphNodeOrGroupLabelFor*Mode` join them, and here rather than
 * behind a resolver because the two modes answer this one identically: the grouping put the
 * labels on the item, so neither has anything to look up.
 */
const toSubLabel = ({ subLabels }: RawGraphNode2): string | null =>
  subLabels?.length ? subLabels.join(', ') : null;

/**
 * A color and whether to write on it in light or dark, resolved together the way the component
 * matrix resolves them — the true component color, not the lightened background variant the
 * old treemap used.
 */
const toColor = (
  color: string | null
): Pick<HierarchicalTreemapNode, 'color' | 'isDark'> => ({
  color,
  isDark: color ? getLuminance(color) < LUMINANCE_THRESHOLD : false,
});

const makeReduceNodes = ({
  resolveLabel,
  resolveRepresentationData,
  resolveColor,
  isComponent,
}: NodeResolvers) => {
  const reduceNodes = (
    state: ReduceNodesState,
    current: RawGraphNode2
  ): ReduceNodesState => {
    /* Resolved once and used for all three, so a group that groups by something other than a
       component carries no icon, no colour and no `data-component-id`. Passing the raw `modelId`
       through sent a component type id to `getComponentRepresentationData`, which logs an error
       for every such group on every emission and hands back a truthy fallback - so the group drew
       a generic component icon, and the id reached the attribute the object context menu reads. */
    const rawModelId = current.modelId ?? null;
    const modelId = rawModelId && isComponent(rawModelId) ? rawModelId : null;
    const node: HierarchicalTreemapNode = {
      id: current.id,
      modelId,
      label: resolveLabel(current),
      subLabel: toSubLabel(current),
      nodes: current.children?.reduce(reduceNodes, { nodes: [] }).nodes ?? null,
      ...toColor(modelId ? resolveColor(modelId) : null),
      representationData: modelId ? resolveRepresentationData(modelId) : null,
    };
    state.nodes.push(node);
    return state;
  };
  return reduceNodes;
};

/**
 * The items to start reducing from. A grouping rule puts everything under a root group, so the
 * groups are the roots — except for components no rule claimed, which are roots in their own
 * right. With no groups at all, every component is.
 */
const toRootItems = ({
  rootGroups,
  componentMap,
}: {
  rootGroups: RawGraphNode2[];
  componentMap: Map<string, RawGraphNode2>;
}): RawGraphNode2[] =>
  rootGroups.length
    ? [
        ...rootGroups,
        ...Array.from(componentMap.values()).filter(
          item => !item.children?.length && !item.parentId
        ),
      ]
    : Array.from(componentMap.values());

export { makeReduceNodes, toRootItems };
export type { NodeResolvers };
